import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { TODAY, addDays, dk, isToday, fmtDate, fmtDow, fmtFull, isOutOnDate, fmtPhone, fmtLastWorked, spanDays } from '../lib/dateUtils'
import { isSchedulable } from '../lib/roster'
import { ROSTER, JOBS, PROJ_COLORS, TASKS } from '../lib/data'
import {
  loadSchedule, saveCell, saveCells,
  loadRequests, saveRequest, updateRequestStatus,
  loadTimeOff, saveTimeOffRecord, deleteTimeOffRecord,
  loadJobs, saveAllJobs, deleteJob as dbDeleteJob,
  loadEmployees, saveAllEmployees, deleteEmployee as dbDeleteEmployee,
  loadJobOrder, saveJobOrder,
  loadUserRole,
} from '../lib/db'
import { createFieldForm } from '../lib/forms'
import Board from '../components/Board'
import { WhosWhere, RosterPanel, Requests, NewRequest, CellModal, TimeOffModal } from '../components/index'
import AdminPanel from '../components/AdminPanel'
import UserManagement from '../components/UserManagement'
import FieldForms from '../components/FieldForms'
import FieldReportsView from '../components/FieldReportsView'
import MyTimeView from '../components/MyTimeView'
import AnnouncementsPanel from '../components/AnnouncementsPanel'
import CertsView from '../components/CertsView'
import TMTicketsView from '../components/TMTicketsView'
import NewHireView from '../components/NewHireView'
import MobileDashboard from './MobileDashboard'

// ── Date helpers ──────────────────────────────────────────────────────────────
function isWeekend(d) { return d.getDay() === 0 || d.getDay() === 6 }

// Every board day a manpower request covers. A freshly submitted request carries
// dateKeys from the form; one loaded from the DB is re-expanded from its stored
// start + days_onsite + the Sat/Sun opt-ins. A legacy single-day row yields [dateKey].
function requestDateKeys(r) {
  if (Array.isArray(r.dateKeys) && r.dateKeys.length) return r.dateKeys
  return spanDays(new Date(r.dateKey), r.daysOnsite || 1,
    { includeSaturday: r.includeSaturday, includeSunday: r.includeSunday }).map(dk)
}

const WORK_DAYS = []
for (let i = 0; i < 44; i++) {
  const d = addDays(TODAY, i - 2)
  WORK_DAYS.push(d)
}

// ── Exported helpers used by child components ─────────────────────────────────
const AVATAR_BG = ['#E6F1FB','#E1F5EE','#FAECE7','#EEEDFE','#FAEEDA','#FBEAF0']
const AVATAR_FG = ['#0C447C','#085041','#712B13','#3C3489','#633806','#72243E']
export function avatarStyle(i) { return { background: AVATAR_BG[i % AVATAR_BG.length], color: AVATAR_FG[i % AVATAR_FG.length] } }
export function initials(name) { return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() }

export { WORK_DAYS }

// ── Static data ───────────────────────────────────────────────────────────────
const INIT_JOBS = JOBS.map((j, i) => ({
  ...j, color: PROJ_COLORS[i % PROJ_COLORS.length], active: true,
}))

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard({ session, isMobile }) {
  const [tab, setTab] = useState('board') // foreman redirect happens after role loads
  const [tmOpenId, setTmOpenId] = useState(null) // ?tab=tm&ticket=<id> from the T&M office email
  const [role, setRole] = useState('foreman') // will be set from DB
  const [roleLoaded, setRoleLoaded] = useState(false)
  const [notSetUp, setNotSetUp] = useState(false) // signed in, but no app_users row
  const [jobs, setJobs] = useState(INIT_JOBS)
  const [roster, setRoster] = useState(ROSTER.map(e => ({ ...e, timeOff: [] })))
  const [cellData, setCellData] = useState({})
  const [requests, setRequests] = useState([])
  const [cellModal, setCellModal] = useState(null)
  const [timeOffModal, setTimeOffModal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [jobOrder, setJobOrder] = useState(null)

  const userId = session?.user?.id
  const userEmail = session?.user?.email || ''
  const [userName, setUserName] = useState('')
  const activeJobs = useMemo(() => jobs.filter(j => j.active !== false), [jobs])
  const pendingCount = requests.filter(r => r.status === 'pending').length

  // ── Load all data from Supabase on mount ────────────────────────────────────
  useEffect(() => {
    async function fetchAll() {
      setLoading(true)
      try {
        // Load jobs (falls back to defaults from data.js if table is empty)
        const dbJobs = await loadJobs()
        if (dbJobs) setJobs(dbJobs)

        // Load employees (falls back to defaults if table is empty)
        const dbEmployees = await loadEmployees()
        const baseRoster = dbEmployees || ROSTER.map(e => ({ ...e, timeOff: [] }))

        // Load time off and merge into roster
        const timeOffRows = await loadTimeOff()
        setRoster(baseRoster.map(emp => ({
          ...emp,
          timeOff: timeOffRows
            .filter(r => r.employee_id === emp.id)
            .map(r => ({ dbId: r.id, from: r.from_date, to: r.to_date, reason: r.reason || '' }))
        })))

        // Load schedule cells
        const cells = await loadSchedule()
        setCellData(cells)

        // Load requests
        const reqs = await loadRequests()
        setRequests(reqs)

        // Load user role
        const userRecord = await loadUserRole(userId)
        if (userRecord) {
          setRole(userRecord.role)
          setUserName(userRecord.name || '')
          if (userRecord.role === 'foreman') setTab('field-reports')
          // If URL has ?form= param, navigate to Field Reports tab
          const params = new URLSearchParams(window.location.search)
          if (params.get('form')) setTab('forms')
          // ?tab= deep link — used by the standalone /report/approve page to get
          // back here. WHITELIST ONLY: 'field-reports' is the one tab every role
          // (foreman included) has. Never honor an arbitrary tab id. Runs after
          // the foreman default so it wins either way.
          if (params.get('tab') === 'field-reports') {
            setTab('field-reports')
            window.history.replaceState({}, '', window.location.pathname)
          }
          // The payroll "new hire" email links to ?tab=new-hires. Honored only
          // while the New Hires build flag is on (every role has that tab then).
          if (params.get('tab') === 'new-hires' && process.env.REACT_APP_NEW_HIRE_ENABLED === 'true') {
            setTab('new-hires')
            window.history.replaceState({}, '', window.location.pathname)
          }
          // The T&M office email links to ?tab=tm&ticket=<id>. Honored only while
          // the T&M build flag is on (every role has the tab then).
          if (params.get('tab') === 'tm' && process.env.REACT_APP_TM_ENABLED === 'true') {
            setTab('tm')
            setTmOpenId(params.get('ticket') || null)
            window.history.replaceState({}, '', window.location.pathname)
          }
        } else {
          // No app_users row: this login was never set up. Logins are created only by the
          // owner-only /api/app-users create action, and the database refuses a self-made
          // row (supabase-role-write-policies.sql), so show "ask the office" instead.
          setNotSetUp(true)
        }
        setRoleLoaded(true)

        // Load job order
        const order = await loadJobOrder()
        if (order) setJobOrder(order)

      } catch (e) {
        console.error('Error loading data:', e)
      }
      setLoading(false)
    }
    fetchAll()
  }, [])

  // ── Cell save — writes to Supabase + updates local state ────────────────────
  const handleSaveCell = useCallback(async (jobId, dateKey, updates, copyDays) => {
    setSaving(true)
    // Build map of all cells to save (this cell + any copy days)
    const toSave = { [dateKey]: updates }
    if (copyDays && copyDays.length > 0) {
      copyDays.forEach(k => { toSave[k] = updates })
    }
    // Update local state immediately so UI feels instant
    setCellData(prev => {
      const next = { ...prev, [jobId]: { ...(prev[jobId] || {}) } }
      Object.entries(toSave).forEach(([k, v]) => { next[jobId][k] = v })
      return next
    })
    // Persist to Supabase (roster enables Phase 5 dual-write of employee ids)
    await saveCells(jobId, toSave, userId, roster)
    setSaving(false)
  }, [userId, roster])

  // ── Time off — save/delete in Supabase ─────────────────────────────────────
  const handleAddTimeOff = useCallback(async (empId, from, to, reason) => {
    const emp = roster.find(e => e.id === empId)
    if (!emp) return
    const dbId = await saveTimeOffRecord(empId, emp.name, from, to, reason, userId)
    setRoster(prev => prev.map(e => e.id === empId
      ? { ...e, timeOff: [...(e.timeOff || []), { dbId, from, to, reason }] }
      : e
    ))
  }, [roster, userId])

  const handleRemoveTimeOff = useCallback(async (empId, idx) => {
    const emp = roster.find(e => e.id === empId)
    if (!emp) return
    const record = emp.timeOff[idx]
    if (record?.dbId) await deleteTimeOffRecord(record.dbId)
    setRoster(prev => prev.map(e => e.id === empId
      ? { ...e, timeOff: e.timeOff.filter((_, i) => i !== idx) }
      : e
    ))
  }, [roster])

  // ── Requests ────────────────────────────────────────────────────────────────
  const handleSubmitRequest = useCallback(async (req) => {
    const dbId = await saveRequest(req, userId)
    const newReq = { ...req, id: dbId || Date.now(), status: 'pending', ts: 'Just now' }
    setRequests(prev => [newReq, ...prev])

    // Fire-and-forget notification to office staff (does not block UI)
    if (dbId) {
      fetch('/api/notify-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobName: req.jobName,
          dateKey: req.dateKey,
          dateLabel: req.date,
          workersRequested: req.workers,
          workersCurrent: req.current,
          requestedBy: req.requestedBy,
          role: req.role,
          reason: req.reason,
          submitterEmail: req.submitterEmail || '',
          daysOnsite: req.daysOnsite || 1,
          includeSaturday: !!req.includeSaturday,
          includeSunday: !!req.includeSunday,
          endDateKey: req.endDateKey || req.dateKey,
        }),
      }).catch(err => console.error('notify-request failed:', err))
    }

    // Write the reason to the task note on the board — every day in the span
    if (req.reason && req.reason !== 'No reason given') {
      const keys = requestDateKeys(req)
      const toSave = {}
      keys.forEach(k => {
        const existing = cellData[req.jobId]?.[k] || { count: 0, foreman: '', task: '', workers: [] }
        toSave[k] = { ...existing, task: req.reason }
      })
      setCellData(prev => ({ ...prev, [req.jobId]: { ...prev[req.jobId], ...toSave } }))
      saveCells(req.jobId, toSave, userId)
    }
    setTab('requests')
  }, [userId, cellData])

  const handleJobOrderChange = useCallback((orderIds) => {
    if (!orderIds) {
      // Reset — clear local state, delete from Supabase
      setJobOrder(null)
      supabase.from('job_order').delete().eq('id', 1).then(({ error }) => {
        if (error) console.error('reset job order error:', error)
      })
    } else {
      setJobOrder(orderIds)
      saveJobOrder(orderIds)
    }
  }, [])

  const handleMoveCell = useCallback(async (jobId, fromDateKey, toDateKey, data) => {
    // Save data to destination day
    await handleSaveCell(jobId, toDateKey, data, [])
    // Clear source day
    await handleSaveCell(jobId, fromDateKey, { count: 0, foreman: '', task: '', workers: [] }, [])
  }, [handleSaveCell])

  const handleApproveRequest = useCallback(async (id) => {
    await updateRequestStatus(id, 'approved')
    const req = requests.find(r => r.id === id)
    if (req) {
      // Set the crew count on every day in the span, preserving each day's own
      // foreman / task / workers (count replaces, it does not add).
      const toSave = {}
      requestDateKeys(req).forEach(k => {
        toSave[k] = {
          ...(cellData[req.jobId]?.[k] || { foreman: '', task: '', workers: [] }),
          count: req.workers,
        }
      })
      setCellData(prev => ({ ...prev, [req.jobId]: { ...(prev[req.jobId] || {}), ...toSave } }))
      setSaving(true)
      await saveCells(req.jobId, toSave, userId, roster)
      setSaving(false)
    }
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r))
  }, [cellData, requests, userId, roster])

  const handleDeleteRequest = useCallback(async (id) => {
    setRequests(prev => {
      const req = prev.find(r => r.id === id)
      if (req) {
        // Clear the task note on the board — every day in the span
        const toSave = {}
        requestDateKeys(req).forEach(k => { toSave[k] = { ...(cellData[req.jobId]?.[k] || {}), task: '' } })
        setCellData(c => ({ ...c, [req.jobId]: { ...c[req.jobId], ...toSave } }))
        saveCells(req.jobId, toSave, userId)
      }
      return prev.filter(r => r.id !== id)
    })
    const { error } = await supabase.from('requests').delete().eq('id', id)
    if (error) console.error('deleteRequest:', error)
  }, [cellData, userId])

  const handleDenyRequest = useCallback(async (id) => {
    await updateRequestStatus(id, 'denied')
    setRequests(prev => {
      const req = prev.find(r => r.id === id)
      if (req) {
        // Clear the task note that was set when request was submitted — every day in the span
        const toSave = {}
        requestDateKeys(req).forEach(k => { toSave[k] = { ...(cellData[req.jobId]?.[k] || {}), task: '' } })
        setCellData(c => ({ ...c, [req.jobId]: { ...c[req.jobId], ...toSave } }))
        saveCells(req.jobId, toSave, userId)
      }
      return prev.map(r => r.id === id ? { ...r, status: 'denied' } : r)
    })
  }, [cellData, userId])

  // ── Jobs admin ──────────────────────────────────────────────────────────────
  const handleUpdateJobs = useCallback((newJobs, deletedJobId) => {
    setJobs(newJobs)
    // Seed empty cell data for any brand new jobs
    setCellData(prev => {
      const next = { ...prev }
      newJobs.filter(j => j.active !== false).forEach(j => {
        if (!next[j.id]) {
          next[j.id] = {}
          WORK_DAYS.forEach(d => {
            next[j.id][dk(d)] = { count: 0, foreman: j.foreman || '', task: '', workers: [] }
          })
        }
      })
      return next
    })
    // Persist to Supabase
    saveAllJobs(newJobs)
    if (deletedJobId) dbDeleteJob(deletedJobId)
  }, [])

  const handleUpdateRoster = useCallback((newRoster, deletedEmpId) => {
    setRoster(newRoster)
    // Persist to Supabase
    saveAllEmployees(newRoster)
    if (deletedEmpId) dbDeleteEmployee(deletedEmpId)
  }, [])

  // ── Summary stats ───────────────────────────────────────────────────────────
  const todayTotal = activeJobs.reduce((s, j) => s + (cellData[j.id]?.[dk(TODAY)]?.count || 0), 0)
  const tomTotal = activeJobs.reduce((s, j) => s + (cellData[j.id]?.[dk(addDays(TODAY, 1))]?.count || 0), 0)
  const outToday = roster.filter(e => isOutOnDate(e, TODAY)).length

  // A foreman gets his own two tabs and nothing else — his reports and the
  // roster. The board, Who's Where and Requests are office surfaces (the board
  // is already read-only for him, but there's no reason to show a foreman the
  // whole company's schedule when he signs in to file his hours).
  // T&M module dark-launch flag: ON in staging env, absent/OFF on live.
  const TM_ON = process.env.REACT_APP_TM_ENABLED === 'true'
  // Certification module dark-launch flag: ON in staging env, absent/OFF on live.
  const CERTS_ON = process.env.REACT_APP_CERTS_ENABLED === 'true'
  // New hire intake dark-launch flag (2026-10-16): ON in staging env, absent/OFF
  // on live. Every role gets the tab (foremen enter new hires; the office
  // reviews) — the API decides what each role may do.
  const NEW_HIRE_ON = process.env.REACT_APP_NEW_HIRE_ENABLED === 'true'

  const tabs = role === 'foreman'
    ? [
        { id: 'field-reports', label: '📋 My Field Reports' },
        ...(TM_ON ? [{ id: 'tm', label: '🎫 T&M Tickets' }] : []),
        ...(NEW_HIRE_ON ? [{ id: 'new-hires', label: '🆕 New Hires' }] : []),
        { id: 'roster', label: 'Roster & time off' },
      ]
    : [
        { id: 'board', label: '2-week board' },
        { id: 'ww', label: "Who's where" },
        { id: 'roster', label: 'Roster & time off' },
        { id: 'requests', label: 'Requests', badge: pendingCount },
        { id: 'new-request', label: '+ New request' },
        { id: 'field-reports', label: '📋 Field Reports' },
        ...(TM_ON ? [{ id: 'tm', label: '🎫 T&M Tickets' }] : []),
        // A PM's own weekly timesheet. Foremen are excluded on purpose — their
        // hours come off the field report their PM approves, not a timesheet.
        ...(role === 'pm' || role === 'owner' || role === 'admin' || role === 'road_super'
          ? [{ id: 'my-time', label: '🕒 My Time' }] : []),
        // Employee safety certifications (Phase 1). Foremen excluded — their
        // upload path is Phase 2; the endpoint 403s them regardless of the tab.
        // CERTS_ON hides the TAB only (like T&M) — the API's role gate is the real guard.
        ...(CERTS_ON && (role === 'pm' || role === 'owner' || role === 'admin' || role === 'road_super')
          ? [{ id: 'certs', label: '🎓 Certifications' }] : []),
        ...(NEW_HIRE_ON ? [{ id: 'new-hires', label: '🆕 New Hires' }] : []),
        ...(role === 'owner' || role === 'road_super' ? [{ id: 'announce', label: '📣 Company text' }] : []),
        ...(role === 'owner' || role === 'road_super' || role === 'admin' ? [{ id: 'admin', label: '⚙ Admin' }] : []),
        ...(role === 'owner' ? [{ id: 'users', label: '👥 Users' }] : []),
      ]

  // ── Login not set up (no app_users row) ─────────────────────────────────────
  if (!loading && notSetUp) return (
    <div className="loading-screen">
      <div className="loading-logo">Crew Dispatch</div>
      <div className="loading-sub" style={{ maxWidth: 360, textAlign: 'center', lineHeight: 1.5 }}>
        Your login isn't set up in Crew Dispatch yet. Ask the office to add you.
      </div>
      <button className="btn btn-sm" style={{ marginTop: 16 }} onClick={() => supabase.auth.signOut()}>Sign out</button>
    </div>
  )

  // ── Mobile render ───────────────────────────────────────────────────────────
  if (!loading && roleLoaded && isMobile) return (
    <MobileDashboard
      session={session}
      activeJobs={activeJobs}
      cellData={cellData}
      roster={roster}
      requests={requests}
      onSubmitRequest={handleSubmitRequest}
      onDeleteRequest={handleDeleteRequest}
      onApproveRequest={handleApproveRequest}
      onSaveCell={handleSaveCell}
      currentUserName={userName}
      currentUserEmail={userEmail}
      role={role}
    />
  )

  // ── Loading screen ──────────────────────────────────────────────────────────
  if (loading) return (
    <div className="loading-screen">
      <div className="loading-logo">Crew Dispatch</div>
      <div className="loading-sub">Loading schedule...</div>
    </div>
  )

  return (
    <div className="app-shell">
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-left">
          <span className="app-logo">Crew Dispatch</span>
          {saving && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Saving...</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20,
              background: role === 'owner' ? '#E6F1FB' : role === 'road_super' ? '#EEE6FB' : role === 'admin' ? '#E2F4F1' : role === 'pm' ? '#EAF3DE' : '#FAEEDA',
              color: role === 'owner' ? '#0C447C' : role === 'road_super' ? '#5B2D9E' : role === 'admin' ? '#0F6E60' : role === 'pm' ? '#3B6D11' : '#854F0B',
              fontWeight: 500 }}>
              {role === 'owner' ? 'Owner' : role === 'road_super' ? 'Road Super' : role === 'admin' ? 'Admin' : role === 'pm' ? 'PM' : 'Foreman'}
            </span>
          </div>
        </div>
        <div className="topbar-right">
          <span className="user-chip">{userEmail}</span>
          <button className="btn btn-sm" onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {tabs.map(t => (
          <div key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
            {t.badge > 0 && <span className="tab-badge">{t.badge}</span>}
          </div>
        ))}
      </div>

      {/* Summary bar */}


      {/* Panels */}
      <div className="main-content" style={{ overflow: tab === 'board' ? 'hidden' : 'auto' }}>
        {tab === 'board' && (
          <Board cellData={cellData} roster={roster} role={role} activeJobs={activeJobs} requests={requests} jobOrder={jobOrder} onJobOrderChange={handleJobOrderChange} onOpenCell={setCellModal} onMoveCell={handleMoveCell} todayTotal={todayTotal} tomTotal={tomTotal} outToday={outToday} pendingCount={pendingCount} rosterCount={roster.filter(isSchedulable).length} />
        )}
        {tab === 'ww' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
            <WhosWhere cellData={cellData} roster={roster} activeJobs={jobs}
              onMarkOut={setTimeOffModal}
              onExportPDF={(dateKey) => exportPDF(dateKey, cellData, roster, activeJobs)}
              role={role} currentUserEmail={userEmail} userId={userId}
              onOpenCell={setCellModal} />
          </div>
        )}
        {tab === 'roster' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
            <RosterPanel roster={roster} onOpenTimeOff={setTimeOffModal} />
          </div>
        )}
        {tab === 'requests' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Requests requests={requests} role={role}
              onApprove={handleApproveRequest} onDeny={handleDenyRequest} onDelete={handleDeleteRequest} />
          </div>
        )}
        {tab === 'new-request' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            <NewRequest jobs={activeJobs} workDays={WORK_DAYS} cellData={cellData}
              role={role} roster={roster}
              currentUserName={userName} currentUserEmail={userEmail}
              onSubmit={handleSubmitRequest} onCancel={() => setTab('board')} />
          </div>
        )}
        {tab === 'announce' && (role === 'owner' || role === 'road_super') && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
            <AnnouncementsPanel />
          </div>
        )}
        {tab === 'admin' && (role === 'owner' || role === 'road_super' || role === 'admin') && (
          <div style={{ padding: 12 }}>
            <AdminPanel roster={roster} jobs={jobs}
              onUpdateRoster={handleUpdateRoster} onUpdateJobs={handleUpdateJobs} />
          </div>
        )}
        {tab === 'users' && role === 'owner' && (
          <div style={{ padding: 12 }}>
            <UserManagement currentUserId={userId} />
          </div>
        )}
        {tab === 'field-reports' && (
          <FieldReportsView
            session={session} role={role}
            userEmail={userEmail} userName={userName} isMobile={false}
          />
        )}
        {tab === 'tm' && TM_ON && (
          <TMTicketsView
            session={session} role={role} activeJobs={activeJobs} cellData={cellData}
            roster={roster} userName={userName} userEmail={userEmail} openTicketId={tmOpenId}
          />
        )}
        {tab === 'my-time' && (role === 'pm' || role === 'owner' || role === 'admin' || role === 'road_super') && (
          <MyTimeView session={session} />
        )}
        {tab === 'certs' && CERTS_ON && (role === 'pm' || role === 'owner' || role === 'admin' || role === 'road_super') && (
          <CertsView session={session} role={role} userEmail={userEmail} />
        )}
        {tab === 'new-hires' && NEW_HIRE_ON && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <NewHireView isMobile={false} />
          </div>
        )}
        {tab === 'forms' && (
          <FieldForms
            role={role} activeJobs={activeJobs} cellData={cellData}
            roster={roster} userEmail={userEmail} userName={userName} userId={userId}
          />
        )}
      </div>

      {/* Cell modal */}
      {cellModal && (
        <CellModal
          job={activeJobs.find(j => j.id === cellModal.jobId)}
          dateKey={cellModal.dateKey}
          cell={cellData[cellModal.jobId]?.[cellModal.dateKey] || { count: 0, foreman: '', task: '', workers: [] }}
          roster={roster}
          cellData={cellData}
          allJobs={activeJobs}
          onSave={(updates, copyDays) => {
            handleSaveCell(cellModal.jobId, cellModal.dateKey, updates, copyDays)
            setCellModal(null)
          }}
          onClose={() => setCellModal(null)}
        />
      )}

      {/* Time off modal — now passes add/remove handlers instead of full roster update */}
      {timeOffModal !== null && (
        <TimeOffModal
          emp={roster.find(e => e.id === timeOffModal)}
          roster={roster}
          onAdd={handleAddTimeOff}
          onRemove={handleRemoveTimeOff}
          onClose={() => setTimeOffModal(null)}
        />
      )}
    </div>
  )
}

// ── PDF export ────────────────────────────────────────────────────────────────
function exportPDF(dateKey, cellData, roster, activeJobs) {
  const dateObj = new Date(dateKey)
  const dateStr = fmtFull(dateObj)
  let totalWorkers = 0, totalJobs = 0
  const outWorkers = roster.filter(e => isOutOnDate(e, dateObj))

  const blocks = activeJobs
    .map(j => ({ j, c: cellData[j.id]?.[dateKey] || { count: 0, foreman: j.foreman || '', task: '', workers: [] } }))
    .filter(({ c }) => c.count > 0)
  blocks.forEach(({ c }) => { totalJobs++; totalWorkers += c.count })

  let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Schedule</title>
  <style>
    body{font-family:Arial,sans-serif;margin:0;padding:22px 28px;color:#111;font-size:14px}
    h1{font-size:20px;font-weight:700;margin:0 0 3px}
    .sub{font-size:13px;color:#555;margin-bottom:18px}
    .summary{display:flex;gap:20px;margin-bottom:18px;padding:10px 14px;background:#f7f7f7;border-radius:6px;border:1px solid #e5e5e5}
    .sum-item{display:flex;flex-direction:column}
    .sum-val{font-size:20px;font-weight:700}
    .sum-lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.5px}
    .proj-block{margin-bottom:20px;border:1px solid #ddd;border-radius:6px;overflow:hidden;page-break-inside:avoid}
    .proj-hd{padding:9px 14px;color:#fff;display:flex;align-items:center;justify-content:space-between}
    .proj-hd-title{font-size:15px;font-weight:700}
    .proj-hd-meta{font-size:12px;opacity:.9}
    .proj-fm{background:#f5f5f5;padding:6px 14px;font-size:13px;border-bottom:1px solid #eee;display:flex;gap:16px}
    .lbl{font-weight:700;color:#555;text-transform:uppercase;font-size:10px;letter-spacing:.4px;margin-right:5px}
    .proj-body{padding:6px 14px 10px}
    .worker-item{font-size:14px;padding:7px 0;border-bottom:.5px solid #ebebeb;display:flex;align-items:center}
    .worker-item:last-child{border-bottom:none}
    .winfo{flex:0 0 auto;min-width:0}
    .wname{font-weight:700;color:#111;font-size:14px}
    .wsep{color:#bbb;margin:0 7px;font-size:13px}
    .wtrade{color:#555;font-size:13px}
    .wsep2{color:#bbb;margin:0 7px;font-size:13px}
    .wphone{color:#333;font-size:13px}
    .wnotes{flex:1;border-bottom:1px solid #ccc;margin-left:20px;min-width:80px;height:1px;align-self:flex-end;margin-bottom:4px}
    .no-crew{color:#aaa;font-style:italic;font-size:13px;padding:8px 0}
    .out-section{margin-top:16px;padding:10px 14px;border:1px solid #fccaca;border-radius:6px;background:#fff5f5}
    .out-title{font-size:12px;font-weight:700;color:#a32d2d;margin-bottom:7px;text-transform:uppercase;letter-spacing:.4px}
    .out-item{font-size:13px;padding:3px 0;color:#a32d2d}
    .footer{margin-top:24px;padding-top:12px;border-top:1px solid #eee;font-size:11px;color:#bbb;text-align:center}
    @media print{.proj-block{page-break-inside:avoid}body{padding:16px 20px}}
  </style>
  </head><body>
  <h1>Daily Crew Schedule</h1>
  <div class="sub">${dateStr} &nbsp;·&nbsp; Generated ${new Date().toLocaleString()}</div>
  <div class="summary">
    <div class="sum-item"><div class="sum-val">${totalWorkers}</div><div class="sum-lbl">Total workers</div></div>
    <div class="sum-item"><div class="sum-val">${totalJobs}</div><div class="sum-lbl">Active sites</div></div>
    <div class="sum-item"><div class="sum-val">${outWorkers.length}</div><div class="sum-lbl">Out</div></div>
  </div>`

  // Build duplicate worker map for PDF
  const workerJobCount = {}
  blocks.forEach(({ c }) => {
    const allW = c.foreman ? [c.foreman, ...(c.workers||[]).filter(w=>w!==c.foreman)] : (c.workers||[])
    allW.forEach(w => { workerJobCount[w] = (workerJobCount[w]||0) + 1 })
  })

  blocks.forEach(({ j, c }) => {
    html += `<div class="proj-block">
      <div class="proj-hd" style="background:${j.color}">
        <div class="proj-hd-title">${j.num} – ${j.name}</div>
        <div class="proj-hd-meta">${c.count} worker${c.count !== 1 ? 's' : ''} · ${j.address || ''}</div>
      </div>
      <div class="proj-fm">
        <span><span class="lbl">Foreman</span>${c.foreman}</span>
        ${c.task ? `<span><span class="lbl">Task</span>${c.task}</span>` : ''}
        ${j.gc ? `<span><span class="lbl">GC</span>${j.gc}</span>` : ''}
      </div>
      <div class="proj-body">`
    const allWorkers = c.foreman ? [c.foreman, ...(c.workers||[]).filter(w=>w!==c.foreman)] : (c.workers||[])
    if (allWorkers.length) {
      allWorkers.forEach(wname => {
        const emp = roster.find(e => e.name === wname) || { trade: '', local: '' }
        const phone = fmtPhone(emp.cellPhone || emp.phone || '')
        const tradeLocal = emp.local || ''
        const isDouble = workerJobCount[wname] > 1
        html += `<div class="worker-item" style="${isDouble ? 'background:#FFF8E1' : ''}">
          <div class="winfo">
            <span class="wname" style="${isDouble ? 'color:#B45309' : ''}">${wname}</span>
            ${isDouble ? '<span style="font-size:11px;color:#B45309;background:#FEF3C7;border-radius:3px;padding:1px 5px;margin-left:6px;font-weight:700">⚠ DOUBLE BOOKED</span>' : ''}
            ${tradeLocal ? `<span class="wsep">–</span><span class="wtrade">${tradeLocal}</span>` : ''}
            ${phone ? `<span class="wsep2">·</span><span class="wphone">${phone}</span>` : ''}
          </div>
        </div>`
      })
    } else {
      html += `<div class="no-crew">No workers assigned</div>`
    }
    html += `</div></div>`
  })

  if (outWorkers.length) {
    html += `<div class="out-section"><div class="out-title">Unavailable – ${outWorkers.length}</div>`
    outWorkers.forEach(e => {
      const phone = e.cellPhone || e.phone || ''
      html += `<div class="out-item">${e.name}${phone ? ' · ' + phone : ''}</div>`
    })
    html += `</div>`
  }

  // Unassigned workers
  const assignedNames = new Set(blocks.flatMap(({ c }) => [...c.workers, c.foreman].filter(Boolean)))
  // Field crew only — see isSchedulable(). Office staff are active but never assignable.
  const unassigned = roster.filter(e => isSchedulable(e) && !assignedNames.has(e.name) && !isOutOnDate(e, dateObj))
  if (unassigned.length) {
    html += `<div style="margin-top:16px;padding:10px 14px;border:1px solid #e5e5e5;border-radius:6px;background:#fafafa">
      <div style="font-size:11px;font-weight:700;color:#555;text-transform:uppercase;letter-spacing:.4px;margin-bottom:8px">Unassigned (${unassigned.length})</div>
      <div style="display:flex;flex-direction:column;gap:0">`
    unassigned.forEach(e => {
      const phone = e.cellPhone || e.phone || ''
      const tradeLocal = e.local || ''
      const lastWorked = fmtLastWorked(e.dateLastWorked)
      html += `<div style="font-size:13px;padding:5px 0;border-bottom:.5px solid #ebebeb;display:flex;align-items:baseline">
        <span style="font-weight:600;color:#111;min-width:180px">${e.name}</span>
        <span style="color:#555;font-size:12px">${tradeLocal}${phone ? ' · ' + phone : ''}${lastWorked ? ' · Last worked: ' + lastWorked : ''}</span>
      </div>`
    })
    html += `</div></div>`
  }

  html += `<div class="footer">Crew Dispatch · ${new Date().toLocaleString()}</div></body></html>`
  const win = window.open('', '_blank')
  if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 500) }
}
